/**
 * @fileoverview Custom Material-UI Theme - Valuation DCF App
 * 
 * @description
 * Theme customizado baseado nos design tokens do website Sétima.
 * Aplica cores, tipografia, espaçamento e componentes customizados.
 * 
 * @source designTokens.ts
 * @date 23 de outubro de 2025
 */

import { createTheme, ThemeOptions } from '@mui/material/styles';
import designTokens from './designTokens';

// ============================================================================
// THEME CONFIGURATION
// ============================================================================

const themeConfig: ThemeOptions = {
  // ---------------------------------------------------------------------------
  // PALETTE (Cores)
  // ---------------------------------------------------------------------------
  palette: {
    mode: 'dark',
    
    primary: {
      main: designTokens.colors.primary.main,
      light: designTokens.colors.primary.light,
      dark: designTokens.colors.primary.dark,
      contrastText: designTokens.colors.primary.contrastText,
    },

    secondary: {
      main: designTokens.colors.secondary.main,
      light: designTokens.colors.secondary.light,
      dark: designTokens.colors.secondary.dark,
      contrastText: designTokens.colors.secondary.contrastText,
    },

    tertiary: {
      main: designTokens.colors.tertiary.main,
      light: designTokens.colors.tertiary.light,
      dark: designTokens.colors.tertiary.dark,
      contrastText: designTokens.colors.tertiary.contrastText,
    },

    background: {
      default: designTokens.colors.dark.main,
      paper: designTokens.colors.dark.elevated,
    },

    text: {
      primary: designTokens.colors.text.primary,
      secondary: designTokens.colors.text.secondary,
      disabled: designTokens.colors.text.disabled,
    },

    error: {
      main: designTokens.colors.error.main,
      light: designTokens.colors.error.light,
      dark: designTokens.colors.error.dark,
      contrastText: designTokens.colors.error.contrastText,
    },

    warning: {
      main: designTokens.colors.warning.main,
      light: designTokens.colors.warning.light,
      dark: designTokens.colors.warning.dark,
      contrastText: designTokens.colors.warning.contrastText,
    },

    success: {
      main: designTokens.colors.success.main,
      light: designTokens.colors.success.light,
      dark: designTokens.colors.success.dark,
      contrastText: designTokens.colors.success.contrastText,
    },

    info: {
      main: designTokens.colors.info.main,
      light: designTokens.colors.info.light,
      dark: designTokens.colors.info.dark,
      contrastText: designTokens.colors.info.contrastText,
    },

    divider: designTokens.colors.border.default,
  },

  // ---------------------------------------------------------------------------
  // TYPOGRAPHY (Tipografia)
  // ---------------------------------------------------------------------------
  typography: {
    fontFamily: designTokens.typography.fontFamily.primary,
    
    h1: {
      fontSize: designTokens.typography.fontSize.h1,
      fontWeight: designTokens.typography.fontWeight.bold,
      lineHeight: designTokens.typography.lineHeight.tight,
    },

    h2: {
      fontSize: designTokens.typography.fontSize.h2,
      fontWeight: designTokens.typography.fontWeight.bold,
      lineHeight: designTokens.typography.lineHeight.snug,
    },

    h3: {
      fontSize: designTokens.typography.fontSize.h3,
      fontWeight: designTokens.typography.fontWeight.medium,
      lineHeight: designTokens.typography.lineHeight.snug,
    },

    h4: {
      fontSize: designTokens.typography.fontSize.h4,
      fontWeight: designTokens.typography.fontWeight.medium,
      lineHeight: designTokens.typography.lineHeight.normal,
    },

    h5: {
      fontSize: designTokens.typography.fontSize.h5,
      fontWeight: designTokens.typography.fontWeight.medium,
      lineHeight: designTokens.typography.lineHeight.normal,
    },

    h6: {
      fontSize: designTokens.typography.fontSize.h6,
      fontWeight: designTokens.typography.fontWeight.medium,
      lineHeight: designTokens.typography.lineHeight.relaxed,
    },

    body1: {
      fontSize: '16px',
      fontWeight: designTokens.typography.fontWeight.regular,
      lineHeight: designTokens.typography.lineHeight.normal,
    },

    body2: {
      fontSize: '14px',
      fontWeight: designTokens.typography.fontWeight.regular,
      lineHeight: 1.43,
    },

    button: {
      fontSize: designTokens.typography.fontSize.bodySmall,
      fontWeight: designTokens.typography.fontWeight.regular,
      textTransform: 'uppercase',
      letterSpacing: designTokens.typography.letterSpacing.wide,
    },
  },

  // ---------------------------------------------------------------------------
  // SHAPE (Bordas e Raios)
  // ---------------------------------------------------------------------------
  shape: {
    borderRadius: parseInt(designTokens.borders.radius.lg), // 12px
  },

  // ---------------------------------------------------------------------------
  // SPACING (Espaçamento)
  // ---------------------------------------------------------------------------
  spacing: designTokens.spacing.unit, // 8px base

  // ---------------------------------------------------------------------------
  // SHADOWS (Sombras)
  // ---------------------------------------------------------------------------
  shadows: [
    'none',
    designTokens.shadows.sm,
    designTokens.shadows.sm,
    designTokens.shadows.md,
    designTokens.shadows.md,
    designTokens.shadows.md,
    designTokens.shadows.lg,
    designTokens.shadows.lg,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
    designTokens.shadows.xl,
  ],

  // ---------------------------------------------------------------------------
  // COMPONENTS (Customizações de Componentes)
  // ---------------------------------------------------------------------------
  components: {
    // -------------------------------------------------------------------------
    // MuiButton
    // -------------------------------------------------------------------------
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borders.radius.pill,
          padding: '12px 24px',
          transition: `all ${designTokens.transitions.duration.base} ${designTokens.transitions.easing.base}`,
          '&:hover': {
            transform: 'translateY(-2px)',
          },
        },
        contained: {
          boxShadow: `0 4px 12px rgba(0, 245, 185, 0.3)`,
          '&:hover': {
            boxShadow: `0 8px 24px rgba(0, 245, 185, 0.5)`,
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
            backgroundColor: 'rgba(0, 245, 185, 0.1)',
          },
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiCard
    // -------------------------------------------------------------------------
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borders.radius['2xl'],
          transition: `all ${designTokens.transitions.duration.base} ${designTokens.transitions.easing.base}`,
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: designTokens.shadows.lg,
          },
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiPaper
    // -------------------------------------------------------------------------
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none', // Remove gradient padrão do MUI
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiTextField
    // -------------------------------------------------------------------------
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: designTokens.borders.radius.md,
            transition: `all ${designTokens.transitions.duration.base} ${designTokens.transitions.easing.base}`,
            '&.Mui-focused fieldset': {
              borderColor: designTokens.colors.primary.main,
              boxShadow: `0 0 0 3px rgba(0, 245, 185, 0.1)`,
            },
          },
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiTooltip
    // -------------------------------------------------------------------------
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: designTokens.colors.dark.elevated,
          border: `1px solid ${designTokens.colors.primary.main}`,
          borderRadius: designTokens.borders.radius.lg,
          padding: '16px',
          fontSize: '14px',
          boxShadow: designTokens.shadows.xl,
        },
        arrow: {
          color: designTokens.colors.primary.main,
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiAlert
    // -------------------------------------------------------------------------
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borders.radius.lg,
        },
        standardInfo: {
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          color: designTokens.colors.text.primary,
        },
        standardSuccess: {
          backgroundColor: 'rgba(0, 245, 185, 0.1)',
          color: designTokens.colors.text.primary,
        },
        standardWarning: {
          backgroundColor: 'rgba(255, 165, 2, 0.1)',
          color: designTokens.colors.text.primary,
        },
        standardError: {
          backgroundColor: 'rgba(255, 71, 87, 0.1)',
          color: designTokens.colors.text.primary,
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiChip (REFATORADO: Sistema Semântico de Badges)
    // -------------------------------------------------------------------------
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borders.radius.sm,
          fontWeight: designTokens.typography.fontWeight.medium,
          fontSize: '13px',
          height: '26px',
        },
        // Chip Info (Azul) - versões, status neutros
        colorInfo: {
          backgroundColor: designTokens.colors.badges.info.bg,
          color: designTokens.colors.badges.info.text,
          border: `1px solid ${designTokens.colors.badges.info.border}`,
        },
        // Chip Success (Verde) - conclusões positivas
        colorSuccess: {
          backgroundColor: designTokens.colors.badges.success.bg,
          color: designTokens.colors.badges.success.text,
          border: `1px solid ${designTokens.colors.badges.success.border}`,
        },
        // Chip Warning (Amarelo) - alertas, atenção
        colorWarning: {
          backgroundColor: designTokens.colors.badges.warning.bg,
          color: designTokens.colors.badges.warning.text,
          border: `1px solid ${designTokens.colors.badges.warning.border}`,
        },
        // Chip Error (Vermelho) - erros, ações destrutivas
        colorError: {
          backgroundColor: designTokens.colors.badges.error.bg,
          color: designTokens.colors.badges.error.text,
          border: `1px solid ${designTokens.colors.badges.error.border}`,
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiTableCell
    // -------------------------------------------------------------------------
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: designTokens.typography.fontWeight.medium,
          fontSize: '14px',
          textTransform: 'uppercase',
          letterSpacing: designTokens.typography.letterSpacing.wide,
          color: designTokens.colors.text.secondary,
        },
        body: {
          fontSize: '16px',
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiBreadcrumbs
    // -------------------------------------------------------------------------
    MuiBreadcrumbs: {
      styleOverrides: {
        separator: {
          color: designTokens.colors.text.tertiary,
        },
      },
    },
  },
};

// ============================================================================
// CREATE THEMES (DARK & LIGHT)
// ============================================================================

/**
 * Dark Theme (MD3) - Modo escuro padrão
 * Baseado em Material Design 3 com seed teal #006A63
 */
export const darkTheme = createTheme(themeConfig);

/**
 * Light Theme (MD3) - Modo claro oficial
 * Material Theme Builder - Seed #006A63
 * Paleta científica com CAM16 HCT
 */
const lightThemeConfig: ThemeOptions = {
  ...themeConfig,
  palette: {
    mode: 'light',
    
    // Primary Teal (Material Theme Builder Official)
    primary: {
      main: '#006A63',              // primary
      light: '#2E847C',             // primary-60
      dark: '#00504A',              // primaryContainer
      contrastText: '#FFFFFF',      // onPrimary
      // MD3 Container System
      container: '#9DF2E8',         // primaryContainer (soft background)
      onContainer: '#00201D',       // onPrimaryContainer (text on container)
      // Shades para backgrounds (MD3)
      50: '#E0F7F5',                // Muito claro para cards/chips
      100: '#B3EDE7',               // Claro
      200: '#80E2D9',               // Médio-claro
    },

    // Secondary (Material Theme Builder Official)
    secondary: {
      main: '#4A6360',              // secondary
      light: '#697B78',             // secondary-50
      dark: '#324B48',              // secondaryContainer variant
      contrastText: '#FFFFFF',      // onSecondary
      // MD3 Container System
      container: '#CCE8E4',         // secondaryContainer (soft background)
      onContainer: '#051F1D',       // onSecondaryContainer (text on container)
    },

    // Tertiary (Material Theme Builder Official)
    tertiary: {
      main: '#47617A',              // tertiary
      light: '#68798B',             // tertiary-50
      dark: '#2F4961',              // tertiaryContainer variant
      contrastText: '#FFFFFF',      // onTertiary
      // MD3 Container System
      container: '#CEE5FF',         // tertiaryContainer (soft background)
      onContainer: '#001D33',       // onTertiaryContainer (text on container)
    },

    // Backgrounds (Material Theme Builder Official)
    background: {
      default: '#F4FBF9',           // background (melhor contraste vs paper)
      paper: '#FFFFFF',             // surface (cards e superfícies)
    },

    // Text (Material Theme Builder Official - Dark on Light)
    text: {
      primary: '#161D1C',           // onBackground (12.3:1 contrast)
      secondary: '#3F4947',         // onSurfaceVariant (7.8:1 contrast)
      disabled: 'rgba(22,29,28,0.38)',  // onSurface 38% opacity
    },

    // Error (Material Theme Builder Official)
    error: {
      main: '#BA1A1A',              // error
      light: '#CF2C27',             // error light-medium-contrast
      dark: '#93000A',              // errorContainer
      contrastText: '#FFFFFF',      // onError
      // MD3 Container System
      container: '#FFDAD6',         // errorContainer (soft background)
      onContainer: '#410002',       // onErrorContainer (text on container)
      // Shades para backgrounds (MD3)
      50: '#FFEEEE',                // Muito claro para cards/chips
      100: '#FFCDD2',               // Claro
      200: '#FFABA8',               // Médio-claro
    },

    // Warning (customizado para light mode)
    warning: {
      main: '#7D5700',
      light: '#A17900',
      dark: '#5E4100',
      contrastText: '#FFFFFF',
      container: '#FFEDB2',         // Warning container suave
      onContainer: '#2A1D00',       // Text on warning container
      // Shades para backgrounds (MD3)
      50: '#FFFBF0',                // Muito claro para cards/chips
      100: '#FFF4D6',               // Claro
      200: '#FFECB3',               // Médio-claro
    },

    // Info (usa tertiary do MD3)
    info: {
      main: '#47617A',
      light: '#68798B',
      dark: '#2F4961',
      contrastText: '#FFFFFF',
      container: '#CEE5FF',
      onContainer: '#001D33',
      // Shades para backgrounds (MD3)
      50: '#EBF3FF',                // Muito claro para cards/chips
      100: '#D6E3FF',               // Claro (= container)
      200: '#B3CCFF',               // Médio-claro
    },

    // Success (usa primary teal do MD3)
    success: {
      main: '#006A63',
      light: '#2E847C',
      dark: '#00504A',
      contrastText: '#FFFFFF',
      container: '#9DF2E8',
      onContainer: '#00201D',
      // Shades para backgrounds (MD3)
      50: '#E0F7F5',                // Muito claro para cards/chips
      100: '#B3EDE7',               // Claro
      200: '#80E2D9',               // Médio-claro
    },

    // Surfaces (Material Theme Builder Official)
    surface: {
      dim: '#D5DBD9',               // surfaceDim
      default: '#F4FBF9',           // surface
      bright: '#F4FBF9',            // surfaceBright
      containerLowest: '#FFFFFF',   // surfaceContainerLowest
      containerLow: '#EFF5F3',      // surfaceContainerLow
      container: '#E9EFED',         // surfaceContainer
      containerHigh: '#E3EAE7',     // surfaceContainerHigh
      containerHighest: '#DDE4E2',  // surfaceContainerHighest
      variant: '#DAE5E2',           // surfaceVariant
      tint: '#006A63',              // surfaceTint (primary)
    },

    // Outline (Material Theme Builder Official)
    outline: {
      main: '#6F7977',              // outline
      variant: '#BEC9C6',           // outlineVariant
    },

    // Inverse (para snackbars e tooltips)
    inverse: {
      surface: '#2B3231',           // inverseSurface
      onSurface: '#ECF2F0',         // inverseOnSurface
      primary: '#81D5CC',           // inversePrimary
    },

    // Cores de cenários (usando containers MD3)
    scenarioColors: {
      pessimista: '#BA1A1A',        // error
      pessimistaContainer: '#FFDAD6',  // errorContainer
      base: '#47617A',              // tertiary
      baseContainer: '#CEE5FF',     // tertiaryContainer
      otimista: '#006A63',          // primary
      otimistaContainer: '#9DF2E8', // primaryContainer
    },

    // Cores de empresas (usando secondary e tertiary)
    companyColors: {
      setima: '#47617A',            // tertiary
      setimaContainer: '#CEE5FF',   // tertiaryContainer
      studios555: '#4A6360',        // secondary
      studios555Container: '#CCE8E4',  // secondaryContainer
      consolidado: '#006A63',       // primary
      consolidadoContainer: '#9DF2E8', // primaryContainer
    },

    // Cores de estados (usando MD3 system)
    statusColors: {
      ok: '#006A63',                // primary
      okContainer: '#9DF2E8',       // primaryContainer
      warning: '#7D5700',           // warning
      warningContainer: '#FFEDB2',  // warningContainer
      error: '#BA1A1A',             // error
      errorContainer: '#FFDAD6',    // errorContainer
    },

    // Shadow & Scrim (Material Theme Builder Official)
    shadow: '#000000',              // shadow
    scrim: '#000000',               // scrim
  },
  
  // ---------------------------------------------------------------------------
  // COMPONENTS OVERRIDES (Light Mode Específico)
  // ---------------------------------------------------------------------------
  components: {
    // -------------------------------------------------------------------------
    // MuiChip - Corrigir Chips Claros (Crescimento forte, Rentabilidade, etc)
    // -------------------------------------------------------------------------
    MuiChip: {
      styleOverrides: {
        // Chip Success (Verde) - CORRIGIDO para light mode
        colorSuccess: {
          backgroundColor: '#B3EDE7',        // success.100 (claro)
          color: '#00504A',                  // success.dark (escuro)
          border: '1px solid #006A63',       // success.main
          fontWeight: 600,
          '&:hover': {
            backgroundColor: '#80E2D9',      // success.200 (hover)
          },
        },
        // Chip Warning (Amarelo) - CORRIGIDO para light mode
        colorWarning: {
          backgroundColor: '#FFF4D6',        // warning.100 (claro)
          color: '#5E4100',                  // warning.dark (escuro)
          border: '1px solid #7D5700',       // warning.main
          fontWeight: 600,
          '&:hover': {
            backgroundColor: '#FFECB3',      // warning.200 (hover)
          },
        },
        // Chip Error (Vermelho) - CORRIGIDO para light mode
        colorError: {
          backgroundColor: '#FFCDD2',        // error.100 (claro)
          color: '#93000A',                  // error.dark (escuro)
          border: '1px solid #BA1A1A',       // error.main
          fontWeight: 600,
          '&:hover': {
            backgroundColor: '#FFABA8',      // error.200 (hover)
          },
        },
        // Chip Info (Azul) - CORRIGIDO para light mode
        colorInfo: {
          backgroundColor: '#D6E3FF',        // info.100 (claro)
          color: '#2F4961',                  // info.dark (escuro)
          border: '1px solid #47617A',       // info.main
          fontWeight: 600,
          '&:hover': {
            backgroundColor: '#B3CCFF',      // info.200 (hover)
          },
        },
        // Chip Primary (Teal) - CORRIGIDO para light mode
        colorPrimary: {
          backgroundColor: '#B3EDE7',        // primary.100 (claro)
          color: '#00504A',                  // primary.dark (escuro)
          border: '1px solid #006A63',       // primary.main
          fontWeight: 600,
          '&:hover': {
            backgroundColor: '#80E2D9',      // primary.200 (hover)
          },
        },
        // Chip Outlined - CORRIGIDO contraste
        outlined: {
          borderWidth: '1.5px',
          fontWeight: 600,
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiAlert - Corrigir Cards de Benchmark/Warning
    // -------------------------------------------------------------------------
    MuiAlert: {
      styleOverrides: {
        standardSuccess: {
          backgroundColor: '#E0F7F5',        // success.50 (muito claro)
          color: '#00504A',                  // success.dark (texto escuro)
          borderLeft: '4px solid #006A63',   // success.main
          '& .MuiAlert-icon': {
            color: '#006A63',                // success.main
          },
        },
        standardWarning: {
          backgroundColor: '#FFFBF0',        // warning.50 (muito claro)
          color: '#5E4100',                  // warning.dark (texto escuro)
          borderLeft: '4px solid #7D5700',   // warning.main
          '& .MuiAlert-icon': {
            color: '#7D5700',                // warning.main
          },
        },
        standardError: {
          backgroundColor: '#FFEEEE',        // error.50 (muito claro)
          color: '#93000A',                  // error.dark (texto escuro)
          borderLeft: '4px solid #BA1A1A',   // error.main
          '& .MuiAlert-icon': {
            color: '#BA1A1A',                // error.main
          },
        },
        standardInfo: {
          backgroundColor: '#EBF3FF',        // info.50 (muito claro)
          color: '#2F4961',                  // info.dark (texto escuro)
          borderLeft: '4px solid #47617A',   // info.main
          '& .MuiAlert-icon': {
            color: '#47617A',                // info.main
          },
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiTooltip - Corrigir Tooltip Escuro em Light Mode
    // -------------------------------------------------------------------------
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#2B3231',        // cinza escuro (readable)
          color: '#FFFFFF',                  // texto branco
          fontSize: '0.875rem',
          padding: '12px 16px',
          maxWidth: 450,
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        },
        arrow: {
          color: '#2B3231',                  // match tooltip bg
        },
      },
    },

    // -------------------------------------------------------------------------
    // MuiButton - Melhorar contraste em fundos coloridos
    // -------------------------------------------------------------------------
    MuiButton: {
      styleOverrides: {
        containedSuccess: {
          backgroundColor: '#006A63',        // success.main
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#00504A',      // success.dark
          },
        },
        containedWarning: {
          backgroundColor: '#7D5700',        // warning.main
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#5E4100',      // warning.dark
          },
        },
        containedError: {
          backgroundColor: '#BA1A1A',        // error.main
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#93000A',      // error.dark
          },
        },
        outlinedSuccess: {
          borderColor: '#006A63',            // success.main
          color: '#006A63',
          '&:hover': {
            backgroundColor: '#E0F7F5',      // success.50
            borderColor: '#00504A',          // success.dark
          },
        },
        outlinedWarning: {
          borderColor: '#7D5700',            // warning.main
          color: '#7D5700',
          '&:hover': {
            backgroundColor: '#FFFBF0',      // warning.50
            borderColor: '#5E4100',          // warning.dark
          },
        },
      },
    },
  },
};

export const lightTheme = createTheme(lightThemeConfig);

/**
 * Tema padrão (dark)
 * Exportado para compatibilidade
 */
export const valuationTheme = darkTheme;

export default valuationTheme;
